import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import PersonAddIcon from "@mui/icons-material/PersonAddAlt1Outlined";
import EditIcon from "@mui/icons-material/EditOutlined";
import BlockIcon from "@mui/icons-material/BlockOutlined";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { CallContact, ResolvedCallPolicy } from "@shared/types/foyer";
import { foyerApi, type RoomTypeRecord } from "../http/foyer";
import CallContactDialog from "./CallContactDialog";
import DangerConfirmDialog from "./DangerConfirmDialog";
import TableSkeleton from "./TableSkeleton";

/**
 * Care → Video calls · Directory (VC-10).
 *
 * ONE list with TWO direction checkboxes, rendered in-row. That is the whole
 * design decision: the same five people appear in both directions, and two
 * separate lists drift - which is exactly how "the relative removed from the
 * visitor list last month can still ring the room" happens. Keeping both flags
 * visible in the table means "can Reception ring rooms?" is answered by looking,
 * not by opening five dialogs.
 *
 * Family contacts are deliberately absent: they hang off the RESIDENT
 * (`scope: "person"`, VC-11), never off a room or the premise, so they are
 * authored in the admission flow and filtered out here even if the API returns
 * them.
 *
 * The table never shows a phone number. The list view carries masks only; the
 * real values are a separate, audited disclosure, so a page load can't quietly
 * become fifty PHI reads.
 */

interface Props {
  premiseId: string;
  /** Premise timezone - prefilled into a contact's quiet hours. */
  premiseTimezone?: string;
}

/** "premise" → every room; "roomType" → one ward, named where we can. */
function scopeLabel(c: CallContact, roomTypes: RoomTypeRecord[]): string {
  if (c.scope === "premise") return "All rooms";
  const rt = roomTypes.find((r) => r.id === c.roomTypeId);
  return rt ? rt.name : (c.roomTypeId ?? "Room type");
}

/** Masked endpoints, e.g. "+46 70 ***** 67 · an***@example.com". */
function endpointSummary(c: CallContact): string {
  const parts: string[] = [];
  if (c.endpointHints?.phone) parts.push(c.endpointHints.phone);
  if (c.endpointHints?.email) parts.push(c.endpointHints.email);
  if (c.endpointKinds?.includes("foyerUser")) parts.push("Foyer user");
  return parts.join(" · ") || "-";
}

/** Human window, or "Always" - the common case, and it should read as calm. */
function windowLabel(c: CallContact): string {
  const fmt = (iso: string) => new Date(iso).toLocaleDateString();
  if (c.activeFrom && c.activeUntil) {
    return `${fmt(c.activeFrom)} - ${fmt(c.activeUntil)}`;
  }
  if (c.activeFrom) return `From ${fmt(c.activeFrom)}`;
  if (c.activeUntil) return `Until ${fmt(c.activeUntil)}`;
  return "Always";
}

export default function CallDirectoryEditor({
  premiseId,
  premiseTimezone,
}: Props) {
  const [contacts, setContacts] = useState<CallContact[] | null>(null);
  const [roomTypes, setRoomTypes] = useState<RoomTypeRecord[]>([]);
  const [policy, setPolicy] = useState<ResolvedCallPolicy | null>(null);
  const [includeRevoked, setIncludeRevoked] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busyRow, setBusyRow] = useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<CallContact | null>(null);
  const [revoking, setRevoking] = useState<CallContact | null>(null);
  const [revokeBusy, setRevokeBusy] = useState(false);
  const [revokeError, setRevokeError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const rows = await foyerApi.listCallContacts(premiseId, {
        includeRevoked,
      });
      // Person-scoped rows are a resident's family; they belong to the
      // admission flow, not to the premise directory.
      setContacts(rows.filter((c) => c.scope !== "person"));
      setError(null);
    } catch (err) {
      setContacts([]);
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [premiseId, includeRevoked]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    foyerApi
      .listRoomTypes()
      .then(setRoomTypes)
      .catch(() => setRoomTypes([]));
  }, []);

  useEffect(() => {
    foyerApi
      .getCallPolicy(premiseId)
      .then(setPolicy)
      .catch(() => setPolicy(null));
  }, [premiseId]);

  const sorted = useMemo(() => {
    const rows = contacts ?? [];
    return [...rows].sort(
      (a, b) =>
        Number(!!a.revokedAt) - Number(!!b.revokedAt) ||
        a.priority - b.priority ||
        a.displayName.localeCompare(b.displayName)
    );
  }, [contacts]);

  /** Flip one direction flag straight from the row - the point of the table. */
  const toggleFlag = async (
    c: CallContact,
    field: "callableFromRoom" | "canCallRoom",
    next: boolean
  ) => {
    setBusyRow(c.id);
    setError(null);
    try {
      await foyerApi.updateCallContact(premiseId, c.id, { [field]: next });
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyRow(null);
    }
  };

  const confirmRevoke = async () => {
    if (!revoking) return;
    setRevokeBusy(true);
    setRevokeError(null);
    try {
      await foyerApi.revokeCallContact(premiseId, revoking.id);
      setRevoking(null);
      setNotice(`${revoking.displayName} revoked`);
      await refresh();
    } catch (err) {
      setRevokeError(err instanceof Error ? err.message : String(err));
    } finally {
      setRevokeBusy(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };
  const openEdit = (c: CallContact) => {
    setEditing(c);
    setDialogOpen(true);
  };

  return (
    <Stack spacing={3} data-testid="call-directory">
      <Box>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ maxWidth: 760 }}
        >
          Staff and services that rooms can reach - and that may ring rooms. Set
          once here; no per-room configuration. Family contacts belong to a
          resident and are added when they’re admitted, on{" "}
          <strong>Residents &amp; door signs</strong>.
        </Typography>
      </Box>

      {error && <Alert severity="error">{error}</Alert>}
      {notice && (
        <Alert severity="success" onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}
      {policy && !policy.enabled && (
        <Alert severity="warning" data-testid="calls-disabled-warning">
          Video calling is switched off for this premise, so nothing in this
          directory reaches a TV yet. Having the video calling product doesn’t
          turn this on by itself - it’s a separate switch under{" "}
          <strong>Config → Video calls → “Video calling enabled”</strong>.
          Entries you add here are kept and take effect the moment that switch
          is turned on.
        </Alert>
      )}

      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
      >
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={includeRevoked}
              onChange={(e) => setIncludeRevoked(e.target.checked)}
              data-testid="include-revoked"
            />
          }
          label="Show revoked"
        />
        <Button
          variant="contained"
          size="small"
          startIcon={<PersonAddIcon />}
          onClick={openCreate}
          data-testid="add-contact"
        >
          Add contact
        </Button>
      </Stack>

      {contacts === null ? (
        <TableSkeleton rows={3} />
      ) : sorted.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No contacts yet. Add “Reception” to give every room someone to call.
        </Typography>
      ) : (
        <Table size="small" data-testid="contacts-table">
          <TableHead>
            <TableRow>
              <TableCell>Contact</TableCell>
              <TableCell>Applies to</TableCell>
              <TableCell align="center">
                <Tooltip title="The contact appears in the room’s call grid, so residents can call them.">
                  <span>Callable from rooms</span>
                </Tooltip>
              </TableCell>
              <TableCell align="center">
                <Tooltip title="The contact may ring a room’s TV.">
                  <span>Can ring rooms</span>
                </Tooltip>
              </TableCell>
              <TableCell>Reached via</TableCell>
              <TableCell>Active</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.map((c) => {
              const revoked = !!c.revokedAt;
              return (
                <TableRow
                  key={c.id}
                  data-testid={`contact-row-${c.id}`}
                  sx={revoked ? { opacity: 0.55 } : undefined}
                >
                  <TableCell>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Avatar
                        src={c.avatarAssetId ?? undefined}
                        sx={{ width: 32, height: 32 }}
                      >
                        {c.displayName.slice(0, 1).toUpperCase()}
                      </Avatar>
                      <Box sx={{ minWidth: 0 }}>
                        <Stack
                          direction="row"
                          spacing={0.75}
                          alignItems="center"
                        >
                          <Typography variant="body2" fontWeight={500}>
                            {c.displayName}
                          </Typography>
                          <Chip
                            size="small"
                            variant="outlined"
                            label={c.kind}
                          />
                          {c.autoAnswer && (
                            <Tooltip title="Joins after an announcement, without the resident accepting.">
                              <Chip
                                size="small"
                                color="warning"
                                label="Auto-answer"
                              />
                            </Tooltip>
                          )}
                          {revoked && (
                            <Chip size="small" color="error" label="Revoked" />
                          )}
                        </Stack>
                        {c.relation && (
                          <Typography variant="caption" color="text.secondary">
                            {c.relation}
                          </Typography>
                        )}
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {scopeLabel(c, roomTypes)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Checkbox
                      size="small"
                      checked={c.callableFromRoom}
                      disabled={revoked || busyRow === c.id}
                      onChange={(e) =>
                        void toggleFlag(c, "callableFromRoom", e.target.checked)
                      }
                      inputProps={
                        {
                          "aria-label": `${c.displayName} callable from rooms`,
                          "data-testid": `callable-${c.id}`,
                        } as React.InputHTMLAttributes<HTMLInputElement>
                      }
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Checkbox
                      size="small"
                      checked={c.canCallRoom}
                      disabled={revoked || busyRow === c.id}
                      onChange={(e) =>
                        void toggleFlag(c, "canCallRoom", e.target.checked)
                      }
                      inputProps={
                        {
                          "aria-label": `${c.displayName} can ring rooms`,
                          "data-testid": `can-ring-${c.id}`,
                        } as React.InputHTMLAttributes<HTMLInputElement>
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {endpointSummary(c)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {windowLabel(c)}
                      {c.quietHours
                        ? ` · quiet ${c.quietHours.from}-${c.quietHours.to}`
                        : ""}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Stack
                      direction="row"
                      spacing={1}
                      justifyContent="flex-end"
                    >
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<EditIcon />}
                        onClick={() => openEdit(c)}
                        disabled={revoked}
                        data-testid={`edit-${c.id}`}
                      >
                        Edit
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        startIcon={<BlockIcon />}
                        onClick={() => {
                          setRevokeError(null);
                          setRevoking(c);
                        }}
                        disabled={revoked}
                        data-testid={`revoke-${c.id}`}
                      >
                        Revoke
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      <CallContactDialog
        open={dialogOpen}
        premiseId={premiseId}
        contact={editing}
        defaultTz={premiseTimezone || "Europe/Stockholm"}
        allowAutoAnswer={policy?.allowAutoAnswer ?? false}
        onClose={() => setDialogOpen(false)}
        onSaved={(msg) => {
          setNotice(msg);
          void refresh();
        }}
      />

      <DangerConfirmDialog
        open={!!revoking}
        title="Revoke this contact?"
        entityName={revoking?.displayName ?? ""}
        confirmLabel="Revoke"
        acknowledgeLabel="I understand this contact will no longer reach any room."
        body={
          <Typography variant="body2">
            They disappear from every room’s call grid and can no longer ring a
            room - both directions, immediately. The entry itself is kept so
            past calls stay explicable in the audit log; it is not deleted.
          </Typography>
        }
        errorMessage={revokeError}
        busy={revokeBusy}
        onConfirm={() => void confirmRevoke()}
        onCancel={() => setRevoking(null)}
      />
    </Stack>
  );
}
